import { useState, useEffect, useRef } from "react";
import { Mail, Phone, MapPin, Send, Clock, MessageCircle, Globe } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";

function useInView(threshold = 0.1) {
  const ref = useRef(null);
  const [isVisible, setIsVisible] = useState(false);
  useEffect(() => {
    const obs = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) setIsVisible(true);
    }, { threshold });
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, [threshold]);
  return [ref, isVisible];
}

function AnimatedSection({ children, className = "", delay = 0 }) {
  const [ref, isVisible] = useInView();
  return (
    <div ref={ref} className={`${className} transition-all duration-700 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`} style={{ transitionDelay: `${delay}ms` }}>
      {children}
    </div>
  );
}

const contactDetails = [
  { icon: Mail, label: "Email", value: "contact@bsmwelfare.org", sub: "We typically respond within 24 hours" },
  { icon: Phone, label: "Phone", value: "+91 XXXXX XXXXX", sub: "Monday to Saturday, 10am – 6pm IST" },
  { icon: MapPin, label: "Office Address", value: "New Delhi, India", sub: "BSM Welfare Foundation Registered Office" },
  { icon: Clock, label: "Working Hours", value: "Mon – Sat: 10:00 AM – 6:00 PM", sub: "Sunday: Closed" },
];

const socialLinks = [
  { icon: Globe, label: "Website", value: "www.bsmwelfare.org" },
  { icon: MessageCircle, label: "WhatsApp", value: "+91 XXXXX XXXXX" },
];

export default function ContactPage() {
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = (e) => {
    e.preventDefault();
    setSubmitted(true);
    setTimeout(() => setSubmitted(false), 3000);
  };

  return (
    <div data-testid="contact-page" className="pt-20">
      {/* Header */}
      <section className="py-20 sm:py-28 bg-[#F3F0E6]">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
          <AnimatedSection>
            <h1 className="font-['Outfit'] text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-[#1B201E] mb-6">Contact Us</h1>
            <p className="text-[#7A827E] text-base sm:text-lg max-w-3xl leading-relaxed">
              We would be glad to hear from you. Whether you are looking to collaborate, volunteer, or simply learn more about our work, feel free to reach out. Our team will respond to your queries at the earliest.
            </p>
          </AnimatedSection>
        </div>
      </section>

      {/* Contact Content */}
      <section data-testid="contact-content" className="py-20 sm:py-28">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-16">
            {/* Contact Form */}
            <div className="lg:col-span-3">
              <AnimatedSection>
                <Card className="border-0 rounded-3xl shadow-[0_4px_24px_rgba(0,0,0,0.04)]">
                  <CardContent className="p-8 sm:p-10">
                    <h2 className="font-['Outfit'] text-2xl font-semibold text-[#1B201E] mb-2">Enquiry Form</h2>
                    <p className="text-sm text-[#7A827E] mb-8">Fill in the form below and we'll get back to you shortly.</p>
                    <form onSubmit={handleSubmit} className="space-y-5" data-testid="contact-form">
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div>
                          <label className="text-sm font-medium text-[#2C3531] mb-1.5 block">Full Name</label>
                          <Input data-testid="contact-name" placeholder="Your full name" className="rounded-xl border-[#E2E5E3] focus:border-[#E28743] focus:ring-[#E28743]/20 h-11" />
                        </div>
                        <div>
                          <label className="text-sm font-medium text-[#2C3531] mb-1.5 block">Email</label>
                          <Input data-testid="contact-email" type="email" placeholder="your@email.com" className="rounded-xl border-[#E2E5E3] focus:border-[#E28743] focus:ring-[#E28743]/20 h-11" />
                        </div>
                      </div>
                      <div>
                        <label className="text-sm font-medium text-[#2C3531] mb-1.5 block">Phone Number</label>
                        <Input data-testid="contact-phone" type="tel" placeholder="+91 XXXXX XXXXX" className="rounded-xl border-[#E2E5E3] focus:border-[#E28743] focus:ring-[#E28743]/20 h-11" />
                      </div>
                      <div>
                        <label className="text-sm font-medium text-[#2C3531] mb-1.5 block">Subject</label>
                        <Input data-testid="contact-subject" placeholder="What's this about?" className="rounded-xl border-[#E2E5E3] focus:border-[#E28743] focus:ring-[#E28743]/20 h-11" />
                      </div>
                      <div>
                        <label className="text-sm font-medium text-[#2C3531] mb-1.5 block">Message</label>
                        <Textarea data-testid="contact-message" placeholder="Your message..." className="rounded-xl border-[#E2E5E3] focus:border-[#E28743] focus:ring-[#E28743]/20 min-h-[140px]" />
                      </div>
                      <button type="submit" data-testid="contact-submit" className="inline-flex items-center gap-2 bg-[#E28743] text-white hover:bg-[#C96A27] rounded-full px-8 py-3.5 font-medium transition-all duration-300 hover:-translate-y-1 shadow-[0_4px_14px_rgba(226,135,67,0.3)] btn-press text-sm">
                        {submitted ? "Message Sent!" : <><Send size={16} /> Send Message</>}
                      </button>
                    </form>
                  </CardContent>
                </Card>
              </AnimatedSection>
            </div>

            {/* Contact Info + Social + Map */}
            <div className="lg:col-span-2 space-y-6">
              <AnimatedSection>
                <h3 className="font-['Outfit'] text-lg font-semibold text-[#1B201E] mb-4">Contact Details</h3>
              </AnimatedSection>
              {contactDetails.map((info, i) => (
                <AnimatedSection key={i} delay={i * 80}>
                  <div data-testid={`contact-info-${i}`} className="flex items-start gap-4 p-5 rounded-2xl bg-white border border-[#E2E5E3]/50 shadow-[0_4px_24px_rgba(0,0,0,0.04)] transition-all duration-300 hover:shadow-[0_10px_30px_rgba(0,0,0,0.06)]">
                    <div className="w-10 h-10 rounded-xl bg-[#E28743]/10 flex items-center justify-center shrink-0">
                      <info.icon size={18} className="text-[#E28743]" strokeWidth={1.5} />
                    </div>
                    <div>
                      <p className="text-xs text-[#7A827E] font-medium uppercase tracking-wider mb-0.5">{info.label}</p>
                      <p className="text-sm font-medium text-[#1B201E]">{info.value}</p>
                      <p className="text-xs text-[#7A827E] mt-0.5">{info.sub}</p>
                    </div>
                  </div>
                </AnimatedSection>
              ))}

              {/* Social Media */}
              <AnimatedSection delay={350}>
                <h3 className="font-['Outfit'] text-lg font-semibold text-[#1B201E] mb-3 mt-8">Connect With Us</h3>
                <div className="space-y-3">
                  {socialLinks.map((link, i) => (
                    <div key={i} data-testid={`social-link-${i}`} className="flex items-center gap-3 p-4 rounded-2xl bg-white border border-[#E2E5E3]/50 shadow-sm">
                      <div className="w-9 h-9 rounded-lg bg-[#606C38]/10 flex items-center justify-center">
                        <link.icon size={16} className="text-[#606C38]" strokeWidth={1.5} />
                      </div>
                      <div>
                        <p className="text-xs text-[#7A827E] font-medium">{link.label}</p>
                        <p className="text-sm text-[#1B201E]">{link.value}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </AnimatedSection>

              {/* Map Placeholder */}
              <AnimatedSection delay={400}>
                <div data-testid="map-placeholder" className="rounded-3xl bg-[#F1F3F2] h-[180px] flex flex-col items-center justify-center border border-[#E2E5E3] mt-6">
                  <MapPin size={28} className="text-[#7A827E] mb-2" strokeWidth={1.5} />
                  <p className="text-sm text-[#7A827E] font-medium">Office Location</p>
                  <p className="text-xs text-[#7A827E]/60 mt-0.5">New Delhi, India</p>
                </div>
              </AnimatedSection>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
